import IntercomInit, { boot, shutdown } from '@intercom/messenger-js-sdk';
import React from 'react';

import { TokenService } from '../../services/authentication/token.service';
import { EnvironmentService } from '../../services/environment/environment.service';
import { useAuthState } from './auth-state';
import type { IntercomApp } from './theme-state';
import { useTheme } from './theme-state';

const tokenService = new TokenService();

function useIntercomEnabled(): boolean {
  const { theme, hasAccessToFeature } = useTheme();
  const isMobile = EnvironmentService.instance.isMobilePlatform();
  const hasAppId = Boolean(theme.intercomApp?.id);
  const hasWidgetAccess = hasAccessToFeature('feature:global:intercom:widget');
  return Boolean(hasAppId && !isMobile && hasWidgetAccess);
}

/** Region for SDK default export: it sets api_base from this. */
function getRegionFromApiBase(apiBase: string): 'us' | 'eu' | 'ap' {
  if (apiBase.includes('eu.intercom')) {
    return 'eu';
  }
  if (apiBase.includes('au.intercom') || apiBase.includes('ap.')) {
    return 'ap';
  }
  return 'us';
}

type IntercomInitSettings = {
  region: 'us' | 'eu' | 'ap';
  app_id: string;
  user_id?: string;
  name?: string;
  email?: string;
  [key: string]: string | number | undefined;
};

function getIntercomInitPayload(
  intercomApp: IntercomApp,
  userPayload: { user_id: string; name: string; email?: string } | undefined,
): IntercomInitSettings {
  return {
    region: getRegionFromApiBase(intercomApp.apiBase),
    app_id: intercomApp.id,
    ...userPayload,
  };
}

/**
 * Handles boot/update for both signed-in users (with identity) and signed-out visitors.
 * Widget is shown whenever Intercom is enabled so unregistered users can access support.
 * Must be mounted inside ThemeStateProvider and AuthStateProvider.
 */
function IntercomStateProvider({ children }: { children: React.ReactNode }) {
  const enabled = useIntercomEnabled();
  const { theme } = useTheme();
  const { status, user } = useAuthState();
  const widgetLoadedRef = React.useRef(false);

  // Boot or update whenever enabled: with user data when authenticated, as visitor when signed out.
  // The SDK requires the default export Intercom(settings) first to load the widget;
  // after that we use boot() to switch between visitor and identified user.
  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const intercomApp: IntercomApp = theme.intercomApp;

    let userPayload: { user_id: string; name: string; email?: string } | undefined;

    if (status === 'authenticated' && user) {
      const tokenSignature = tokenService.getIntercomSignature();
      if (tokenSignature) {
        userPayload = {
          user_id: tokenSignature.identifier,
          name: tokenSignature.name,
          email: tokenSignature.email,
        };
      } else {
        userPayload = {
          user_id: user.id,
          name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email,
          email: user.email,
        };
      }
    }

    const initSettings = getIntercomInitPayload(intercomApp, userPayload);

    if (!widgetLoadedRef.current) {
      // First time: use default export so the SDK sets window.intercomSettings and loads the widget
      IntercomInit(initSettings);
      widgetLoadedRef.current = true;
    } else {
      // Already loaded: boot with current identity (user or visitor)
      boot({
        app_id: intercomApp.id,
        api_base: intercomApp.apiBase,
        ...userPayload,
      });
    }
  }, [enabled, status, user, theme.intercomApp]);

  return <>{children}</>;
}

export interface IntercomContextValue {
  shutdown: () => void;
  isEnabled: boolean;
}

const IntercomContext = React.createContext<IntercomContextValue | undefined>(undefined);

/**
 * Provides shutdown and isEnabled for components (e.g. sign-out).
 * Wrap with IntercomStateProvider so context is set.
 */
export function IntercomProviderWithContext({ children }: { children: React.ReactNode }) {
  const enabled = useIntercomEnabled();

  const value = React.useMemo<IntercomContextValue>(
    () => ({
      shutdown: () => {
        if (enabled) {
          shutdown();
        }
      },
      isEnabled: enabled,
    }),
    [enabled],
  );

  return (
    <IntercomContext.Provider value={value}>
      <IntercomStateProvider>{children}</IntercomStateProvider>
    </IntercomContext.Provider>
  );
}

export function useOptionalIntercomState(): IntercomContextValue | undefined {
  return React.useContext(IntercomContext);
}
